/* eslint-disable camelcase */
import React, { useContext, useEffect, useRef, useState } from 'react'
import Buttons from '../../../../components/buttons/buttons'
import Noresult from '../../../../components/noresult/noresult'
import InputSearch from '../../../../utils/controls/input-search'
import ActionButton from '../../../../components/buttons/newbutton'
import { PopupCloseContext, UserInitalValue } from '../../../../shared/contexts/popupclose-context'
import Popups from '../../../../components/popup/popupwithposition'
import InputText from '../../../../utils/controls/input-text'
import Alert from '../../../../components/alert/alert'
import SpPeoplePicker from 'react-sp-people-picker'
import Tooltip from '../../../../components/tooltip/tooltips'
import { useMsal } from '@azure/msal-react'
import { apiRequest, loginRequest } from '../../../../authConfig'
import axios from 'axios'
import { BASE_URL } from '../../../../configuration'

interface GraphUser { id: string; mail: string; displayName: string; givenName: string; surname: string; }
interface AppUser { id: string; name: string; email: string; }

const BusinessownersUM = (props: { onUsersFetched: (count: number) => void }) => {
    const { instance, accounts } = useMsal()
    const [users, setUsers] = useState<AppUser[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [searchResults, setSearchResults] = useState<GraphUser[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const [newUser, setNewUser] = useState<GraphUser | null>(null)
    const [popupError, setPopupError] = useState<string>('')

    const { multiplePopup, setMultiplePopup }: typeof UserInitalValue = useContext(PopupCloseContext)
    const addUserPopupRef = useRef<HTMLButtonElement>(null)
    const settingsTableHeaderRef = useRef<any>(null)

    const getAccessToken = async (request: any) => {
        if (accounts.length > 0) {
          try {
            const response = await instance.acquireTokenSilent({ ...request, account: accounts[0] })
            return response.accessToken
          } catch (e) {
            return instance.acquireTokenPopup(request).then(res => res.accessToken).catch(() => null)
          }
        }
        return null
    }

    const fetchUsers = async () => {
        setIsLoading(true); setError(null)
        const token = await getAccessToken(apiRequest)
        if (!token) { setError('Authentication failed.'); setIsLoading(false); return }
        try {
          const headers = { Authorization: `Bearer ${token}` }
          const response = await axios.get(`${BASE_URL}/users/roles`, { headers })
          
          const userList = response.data.data || []
          
          const formattedUsers = userList.map((user: any) => ({
            id: user.user_id,
            name: user.name,
            email: user.email,
          }))

          setUsers(formattedUsers)
          props.onUsersFetched(formattedUsers.length)
        } catch (e: any) {
          setError('Failed to fetch users. ' + e.message)
        } finally {
          setIsLoading(false)
        }
    }

    useEffect(() => {
        if (accounts.length > 0) {
            fetchUsers()
        }
    }, [accounts, instance])

    const addUserClick = (ele: string | null) => {
        setMultiplePopup((prev) => (prev === ele ? null : ele))
        if (ele) { setPopupError(''); setNewUser(null); setSearchQuery(''); setSearchResults([]) }
    }
    
    const handleSaveUser = async () => {
        if (!newUser) { setPopupError('Please search for and select a user.'); return }
        const token = await getAccessToken(apiRequest)
        if (!token) { setPopupError('Authentication failed.'); return }
        
        const payload = {
            user_id: newUser.id, user_email: newUser.mail,
            first_name: newUser.givenName, last_name: newUser.surname, user_name: newUser.displayName
        }
        const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
        
        try {
            await axios.post(`${BASE_URL}/update-roles`, payload, { headers })
            addUserClick(null)
            fetchUsers()
        } catch (error: any) {
            setPopupError(error.message)
        }
    }

    const handleDeleteUser = async (userId: string) => {
        if (!window.confirm('Are you sure you want to remove this owner?')) return
        const token = await getAccessToken(apiRequest)
        if (!token) { setError('Authentication failed.'); return }

        const headers = { Authorization: `Bearer ${token}` }
        const config = { headers, params: { user_id: userId } } // Pass user_id as a query parameter
        
        try {
          await axios.delete(`${BASE_URL}/users/roles`, config)
          fetchUsers()
        } catch (error: any) {
          setError(error.message)
          alert(`Error deleting user: ${error.response?.data?.message || error.message}`)
        }
    }
    
    const handleUserSelect = (selectedUser: GraphUser) => {
        setPopupError('')
        if (users.some(u => u.email.toLowerCase() === selectedUser.mail.toLowerCase())) {
            setPopupError('This user is already an owner.'); setNewUser(null)
        } else {
            setNewUser(selectedUser); setSearchQuery(selectedUser.displayName); setSearchResults([])
        }
    }

    useEffect(() => {
        const searchGraph = async () => {
          if (searchQuery.length < 3) { setSearchResults([]); return }
          setIsSearching(true)
          const token = await getAccessToken(loginRequest)
          if (!token) { setIsSearching(false); return }
          try {
            const endpoint = `https://graph.microsoft.com/v1.0/users?$filter=startswith(displayName,'${searchQuery}') or startswith(mail,'${searchQuery}')&$select=id,displayName,mail,givenName,surname&$top=10`
            // Graph API calls should not use BASE_URL, so this remains fetch or can be a separate axios instance.
            const response = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } })
            const data = await response.json()
            setSearchResults(data.value || [])
          } catch (error: any) { console.error('Graph API search failed', error) }
          finally { setIsSearching(false) }
        }
        const debounce = setTimeout(() => {
          if (!newUser || searchQuery !== newUser.displayName) searchGraph()
        }, 500)
        return () => clearTimeout(debounce)
      }, [searchQuery])

    return (
        <>
            <div className='row m-0'>
                <div className='settings-title mt-0 p-0 d-flex justify-content-between align-items-center flex-wrap px-2 pe-3 gap-1'>
                    <h2 tabIndex={0} aria-label='User Management' className='segoeui-semibold font-16 m-0'>User Management</h2>
                    <div className='d-flex align-items-center gap-2 ms-auto p-relative'>
                        <InputSearch inputProps={{ arialabel: 'search', placeholder: 'Search' }} formClassName='min-w-250 max-w-250 ms-3' className=' border-radius3 w-100 font-14 bordered2' searchgroup='w-100 d-flex align-items-center inputswithlicon' icon='icon-search px-2 icon-primarycolor' />
                        <ActionButton label='Add' name='Add' className='btn-sm btn-primary whitetext text-nowrap segoeui-semibold p-relative' icon='icon-add pe-1 font-10' type='button' aria-expanded={multiplePopup === 'addUserPopup'} ref={addUserPopupRef} onClick={() => addUserClick('addUserPopup')} />
                        {multiplePopup === 'addUserPopup' && (
                            <Popups buttonRef={addUserPopupRef} popupId='addUserPopup' className='dropdown-popup popup-sm border-bottom-left-radius2 border-bottom-right-radius2 border-radius0 card shadow popup-bordertop1 fade-in ' popupContent={
                                <>
                                    <div className='popup-lg'>
                                        <h5 className='segoeui-semibold font-16 p-3 pb-0'>Add User</h5>
                                        <div className='card-body p-3'>
                                            <label htmlFor='AddUserInput' className='segoeui-regular font-14 darktext'>Add User <span className='mandatory'>*</span> <Tooltip content='Add User' position='top'><span className='icon-info color-primary'></span></Tooltip> </label>
                                            <div className='d-flex align-items-center sppeoplepicker p-relative'>
                                                <input id='AddUserInput' type='text' className='form-control w-100' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder='Start typing name or email address...' autoComplete='off' />
                                                {isSearching && <div className='search-results-popup'>Searching...</div>}
                                                {!isSearching && searchResults.length === 0 && searchQuery.length >= 3 && !newUser && <div className='search-results-popup'>No matching user found!</div>}
                                                {searchResults.length > 0 && (
                                                    <ul className='search-results-popup card'>
                                                    {searchResults.map((user: GraphUser) => (
                                                        <li key={user.id} onClick={() => handleUserSelect(user)}>
                                                        <div className='fw-bold'>{user.displayName}</div>
                                                        <div className='text-muted small'>{user.mail}</div>
                                                        </li>
                                                    ))}
                                                    </ul>
                                                )}
                                            </div>
                                            {popupError && <p className='errormsg mt-2'>{popupError}</p>}
                                        </div>
                                        <div className='card-footer pt-0 p-3 d-flex align-items-center gap-1 justify-content-end'>
                                            <Buttons label='Save' className='btn-xs segoeui-semibold py-1 font-13 px-3 btn-primary ms-2 whitetext' onClick={handleSaveUser} />
                                            <Buttons label='Cancel' className='btn-xs segoeui-semibold font-13 py-1 px-3 btn-border1' onClick={() => addUserClick(null)} />
                                        </div>
                                    </div>
                                </>
                            }/>
                        )}
                    </div>
                </div>
                <div className='striped-table m-0 p-0 striped-table overflow-auto'>
                    <table>
                        <thead ref={settingsTableHeaderRef}><tr><th tabIndex={0} aria-label='Name'>Name</th><th tabIndex={0} aria-label='Email' colSpan={2}>Email</th></tr></thead>
                        <tbody>
                            {users && users.length > 0 ?
                                users.map((item: AppUser) => (
                                    <tr key={item.id}>
                                        <td tabIndex={0}>{item.name}</td>
                                        <td tabIndex={0}>{item.email}</td>
                                        <td><Buttons label='Delete' aria-label={`Delete ${item.name}`} icon='icon-trashbin me-xl-1 font-12' className='btn-border btn-xs font-13 btn-border-radius3 ms-auto color-primary text-nowrap' onClick={() => handleDeleteUser(item.id)}/></td>
                                    </tr>
                                )) : <tr><td colSpan={3}><div className='min-h-200 d-flex align-items-center justify-content-around'>{isLoading ? <p>Loading...</p> : <Noresult />}</div></td></tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    )
}
export default BusinessownersUM